// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Reports Page
// Route: /reports
// apps/web/src/routes/reports.tsx
//
// Generate and manage R&D reports: IRS Tax Credit, Investor Summary,
// Engineering Velocity, CPA Export, VC Data Room. Download in PDF, XLSX,
// CSV, JSON. Shows generation status and history.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useReports, useReportMutations } from '../lib/api/effort-reports';
import { useProjects } from '../lib/api/projects';
import { useSubscription, normalizeTier, meetsMinTier, type SubscriptionTier } from '../lib/api/billing';
import type { Report, ReportType, ExportFormat, GenerateReportRequest } from '../lib/api/effort-reports';

export const Route = createFileRoute('/reports')({
  component: ReportsPage,
});

// ─── Config ─────────────────────────────────────────────────────────────────

const REPORT_TYPE_CONFIG: Record<ReportType, { color: string; icon: string; label: string; desc: string; requiredTier: SubscriptionTier }> = {
  RDCreditNarrative: { color: C.neon, icon: '📋', label: 'R&D Tax Credit', desc: '4-part test narratives, qualified expenses, period totals (jurisdiction-aware)', requiredTier: 'Builder' },
  InvestorSummary: { color: C.amber, icon: '📊', label: 'Investor Summary', desc: 'Compression ratios, replacement cost, velocity metrics', requiredTier: 'Professional' },
  EngineeringVelocity: { color: C.cyan, icon: '⚡', label: 'Engineering Velocity', desc: 'Weekly/monthly velocity trends, AI cost analysis', requiredTier: 'Builder' },
  CPAExport: { color: C.blue, icon: '📑', label: 'CPA Work Papers', desc: 'Structured data export matching CPA work paper formats', requiredTier: 'Professional' },
  VCDataRoom: { color: C.violet, icon: '🏦', label: 'VC Data Room', desc: 'Complete project history, decision log, team metrics', requiredTier: 'Enterprise' },
  Custom: { color: C.pink, icon: '🔧', label: 'Custom Report', desc: 'User-defined date range and metrics selection', requiredTier: 'Builder' },
};

const FORMAT_CONFIG: Record<ExportFormat, { icon: string; color: string }> = {
  PDF: { icon: '📄', color: C.coral },
  XLSX: { icon: '📊', color: C.neon },
  CSV: { icon: '📋', color: C.cyan },
  JSON: { icon: '{ }', color: C.amber },
  HTML: { icon: '🌐', color: C.violet },
};

const STATUS_COLOR: Record<string, string> = {
  Generating: C.amber,
  Ready: C.neon,
  Failed: C.coral,
};

// ─── Main Component ─────────────────────────────────────────────────────────

function ReportsPage() {
  const { data: reports, loading, refetch } = useReports();
  const { data: projects } = useProjects();
  const { data: subscription } = useSubscription();
  const currentTier = normalizeTier(subscription?.tier);
  const mutations = useReportMutations();
  const [showGenerate, setShowGenerate] = useState(false);

  const countReports = useCounter(reports?.length ?? 0, 500, 200);
  const readyCount = useMemo(() => reports?.filter(r => r.status === 'Ready').length ?? 0, [reports]);
  const generatingCount = useMemo(
    () => reports?.filter(r => r.status === 'Generating').length ?? 0,
    [reports]
  );

  // Poll the reports list every 5s while any AI report is still
  // generating. Reports take 4-7 minutes against gpt-5.5; without
  // this the user sits looking at a "Generating" badge that never
  // updates until they manually refresh. Stops polling when no rows
  // are in-flight to avoid burning network for nothing.
  useEffect(() => {
    if (generatingCount === 0) return;
    const id = window.setInterval(() => { refetch(); }, 5000);
    return () => window.clearInterval(id);
  }, [generatingCount, refetch]);

  return (
    <EnginePageShell
      title="Reports"
      subtitle="R&D DOCUMENTATION"
      icon="📊"
      accentColor={C.blue}
      actions={
        <button
          onClick={() => setShowGenerate(true)}
          style={{
            background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
            fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '6px 16px',
            borderRadius: 6, cursor: 'pointer', letterSpacing: '0.08em',
          }}
        >
          + GENERATE REPORT
        </button>
      }
    >
      {/* ── Metrics ───────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        <MetricCard label="Total Reports" value={countReports} icon="📊" color={C.blue}
          tooltip={<div>Reports generated across all projects.</div>} />
        <MetricCard label="Ready" value={readyCount} icon="✓" color={C.neon}
          tooltip={<div>Reports ready for download.</div>} />
      </div>

      {/* ── Report Type Cards ─────────────────────────────────────────── */}
      <SectionLabel color={C.violet}>Available Report Types</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, marginBottom: 28 }}>
        {(Object.entries(REPORT_TYPE_CONFIG) as [ReportType, typeof REPORT_TYPE_CONFIG[ReportType]][]).map(([type, cfg], i) => {
          const locked = !meetsMinTier(currentTier, cfg.requiredTier);
          return (
            <GlassPanel key={type} accent={locked ? C.dim : cfg.color} delay={0.2 + i * 0.05} style={{ padding: 14, opacity: locked ? 0.55 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 22 }}>{cfg.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: locked ? C.dim : C.text }}>{cfg.label}</span>
                    {locked && (
                      <span style={{
                        fontSize: 9, fontFamily: F.mono, letterSpacing: '0.1em',
                        color: C.amber, background: `${C.amber}15`, border: `1px solid ${C.amber}30`,
                        borderRadius: 4, padding: '2px 6px',
                      }}>
                        {cfg.requiredTier.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>{cfg.desc}</div>
                </div>
              </div>
            </GlassPanel>
          );
        })}
      </div>

      {/* ── Generated Reports List ────────────────────────────────────── */}
      <SectionLabel color={C.blue}>Generated Reports</SectionLabel>
      {loading ? (
        <GlassPanel delay={0.5}><LoadingShimmer height={150} /></GlassPanel>
      ) : reports && reports.length > 0 ? (
        <GlassPanel accent={C.blue} delay={0.5} style={{ padding: 0, overflow: 'hidden' }}>
          {reports.map((report, i) => (
            <ReportRow key={report.id} report={report} index={i} onDownload={async () => {
              const url = await mutations.downloadUrl(report.id);
              if (url) window.open(url, '_blank');
            }} />
          ))}
        </GlassPanel>
      ) : (
        <GlassPanel accent={C.blue} delay={0.5}>
          <EmptyState icon="📊" title="No reports generated yet"
            subtitle="Generate your first report to create IRS-ready tax credit documentation, investor summaries, or engineering velocity dashboards."
            action={
              <button onClick={() => setShowGenerate(true)} style={{
                background: `${C.neon}15`, border: `1px solid ${C.neon}40`, color: C.neon,
                fontFamily: F.mono, fontSize: 12, padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
              }}>GENERATE FIRST REPORT</button>
            }
          />
        </GlassPanel>
      )}

      {/* ── Generate Modal ────────────────────────────────────────────── */}
      {showGenerate && (
        <GenerateReportModal
          projects={projects || []}
          currentTier={currentTier}
          onClose={() => setShowGenerate(false)}
          onGenerate={async (data) => {
            const result = await mutations.generate(data);
            if (result) { setShowGenerate(false); refetch(); }
          }}
          loading={mutations.loading}
          error={mutations.error?.message}
        />
      )}
    </EnginePageShell>
  );
}

// ─── ReportRow ──────────────────────────────────────────────────────────────

function ReportRow({ report, index, onDownload }: { report: Report; index: number; onDownload: () => void }) {
  const [hovered, setHovered] = useState(false);
  const typeCfg = REPORT_TYPE_CONFIG[report.reportType] || REPORT_TYPE_CONFIG.Custom;
  const fmtCfg = FORMAT_CONFIG[report.exportFormat] || FORMAT_CONFIG.PDF;
  const statusColor = STATUS_COLOR[report.status] || C.dim;

  return (
    <div
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
        borderBottom: `1px solid ${C.faint}`, background: hovered ? 'rgba(16,28,52,0.4)' : 'transparent',
        transition: 'background 0.15s', opacity: 0,
        animation: `engine-feed-in 0.3s ease ${0.6 + index * 0.04}s forwards`,
      }}
    >
      <span style={{ fontSize: 18 }}>{typeCfg.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{typeCfg.label}</span>
          <StatusBadge label={report.status} color={statusColor} glow={report.status === 'Generating'} />
          <span style={{ fontSize: 10, fontFamily: F.mono, color: fmtCfg.color, background: `${fmtCfg.color}10`,
            border: `1px solid ${fmtCfg.color}20`, padding: '1px 6px', borderRadius: 3,
          }}>{report.exportFormat}</span>
        </div>
        <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginTop: 3 }}>
          {report.projectName || `Project #${report.projectId}`} ·{' '}
          {new Date(report.generatedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          {report.periodStart && ` · ${report.periodStart.slice(0, 10)} — ${report.periodEnd.slice(0, 10)}`}
        </div>
      </div>
      {report.status === 'Ready' && (
        <button
          onClick={(e) => { e.stopPropagation(); onDownload(); }}
          style={{
            background: `${C.neon}12`, border: `1px solid ${C.neon}30`, color: C.neon,
            fontFamily: F.mono, fontSize: 10, padding: '6px 14px', borderRadius: 6,
            cursor: 'pointer', fontWeight: 600, letterSpacing: '0.05em',
          }}
        >
          ↓ DOWNLOAD
        </button>
      )}
    </div>
  );
}

// ─── GenerateReportModal ────────────────────────────────────────────────────

function GenerateReportModal({ projects, currentTier, onClose, onGenerate, loading, error }: {
  projects: { id: number; name: string }[];
  currentTier: SubscriptionTier;
  onClose: () => void;
  onGenerate: (data: GenerateReportRequest) => void;
  loading: boolean;
  error?: string | null;
}) {
  const [projectId, setProjectId] = useState(0);
  const [reportType, setReportType] = useState<ReportType>('RDCreditNarrative');
  const [format, setFormat] = useState<ExportFormat>('PDF');

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
    borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
    fontSize: 13, outline: 'none', appearance: 'none' as const,
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'engine-tooltip-in 0.2s ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.panel, border: `1px solid ${C.blue}20`, borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 520,
      }}>
        <SectionLabel color={C.blue}>Generate Report</SectionLabel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>PROJECT *</div>
            <select value={projectId} onChange={e => setProjectId(Number(e.target.value))} style={inputStyle}>
              <option value={0}>Select project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>REPORT TYPE</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
              {(Object.entries(REPORT_TYPE_CONFIG) as [ReportType, typeof REPORT_TYPE_CONFIG[ReportType]][]).map(([type, cfg]) => {
                const locked = !meetsMinTier(currentTier, cfg.requiredTier);
                const selected = reportType === type && !locked;
                return (
                  <button key={type} onClick={() => !locked && setReportType(type)} style={{
                    background: selected ? `${cfg.color}15` : 'transparent',
                    border: `1px solid ${selected ? `${cfg.color}40` : C.faint}`,
                    color: locked ? C.dim : selected ? cfg.color : C.muted,
                    fontFamily: F.mono, fontSize: 10, padding: '8px 12px', borderRadius: 6,
                    cursor: locked ? 'not-allowed' : 'pointer', textAlign: 'left',
                    opacity: locked ? 0.5 : 1,
                  }}>
                    {cfg.icon} {cfg.label}
                    {locked && <span style={{ marginLeft: 4, fontSize: 8, color: C.amber }}>[{cfg.requiredTier.toUpperCase()}]</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>FORMAT</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {(Object.keys(FORMAT_CONFIG) as ExportFormat[]).map(fmt => {
                const cfg = FORMAT_CONFIG[fmt];
                return (
                  <button key={fmt} onClick={() => setFormat(fmt)} style={{
                    background: format === fmt ? `${cfg.color}20` : 'transparent',
                    border: `1px solid ${format === fmt ? `${cfg.color}50` : C.faint}`,
                    color: format === fmt ? cfg.color : C.muted,
                    fontFamily: F.mono, fontSize: 11, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                  }}>
                    {fmt}
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div style={{ fontSize: 12, color: C.coral, padding: '8px 12px', background: `${C.coral}10`, borderRadius: 6 }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${C.faint}`, color: C.muted, fontFamily: F.mono, fontSize: 11, padding: '8px 20px', borderRadius: 6, cursor: 'pointer' }}>CANCEL</button>
          <button
            disabled={!projectId || loading}
            onClick={() => onGenerate({ projectId, reportType, exportFormat: format })}
            style={{
              background: projectId ? `${C.neon}20` : `${C.dim}10`,
              border: `1px solid ${projectId ? `${C.neon}40` : C.faint}`,
              color: projectId ? C.neon : C.dim,
              fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '8px 24px',
              borderRadius: 6, cursor: projectId ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'GENERATING...' : 'GENERATE'}</button>
        </div>
      </div>
    </div>
  );
}
